import {
  ChangeDetectionStrategy,
  Component,
  HostListener,
  Injector,
  ViewEncapsulation,
} from '@angular/core';
import { Router, RouterLink, RouterOutlet } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { AppInjector } from '@sc/data-access-app-injector';
import { ConfigurationService } from '@sc/data-access-configuration';
import {
  DossierSandboxService,
  SandboxService,
} from '@sc/data-access-state-management';
import { AuthOktaService } from '@sc/feature-auth-okta';
import { FocusManagerService } from '@sc/feature-focus-manager';
import { MainLoaderComponent } from '@sc/feature-loader';
import { TagManagerService } from '@sc/feature-tag-manager';
import { filter, take } from 'rxjs/operators';

@Component({
  selector: 'sc-root-customer',
  styleUrls: ['./app.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MainLoaderComponent, RouterLink, RouterOutlet, TranslateModule],
  template: `
    <sc-main-loader></sc-main-loader>
    <a
      [routerLink]="[getRouterLink()]"
      queryParamsHandling="preserve"
      fragment="sc-container-id"
      (click)="onLinkClick('sc-container-id')"
      >{{
        'GlobalApplicativeConstants.accessibility.avoidanceLink' | translate
      }}</a
    >
    <router-outlet></router-outlet>
  `,
})
export class AppComponent {
  constructor(
    private readonly injector: Injector,
    public configurationService: ConfigurationService,
    private readonly sb: SandboxService,
    private readonly dossierSB: DossierSandboxService,
    private readonly authService: AuthOktaService,
    private readonly tmService: TagManagerService,
    private readonly route: Router,
    private readonly focusManager: FocusManagerService
  ) {
    AppInjector.setInjector(this.injector);

    this.dossierSB.listenLanguageUpdate();

    this.tmService.subscribeToRouterStates();

    this.dossierSB.dossier$
      .pipe(
        filter((d) => !!d),
        take(1)
      )
      .subscribe((dossier) => {
        if (dossier?.creationData?.businessOrigin) {
          this.tmService?.pushTag({
            event: 'new_customer_access',
            businessOrigin: dossier.creationData.businessOrigin,
          });
        }
        if (
          this.configurationService.getConfig().customTagManagerData?.resources
            ?.length
        ) {
          this.tmService?.pushTag({});
        }
      });

    this.authService.authSubject$.subscribe((isauth) => {
      if (isauth) this.sb.checkSubscription();
      this.sb.setIsAuth(isauth);
    });

    // Suppress OneTrust JSON parse errors caused by CDN issues wich blocks app execution
    window.addEventListener('error', (event) => {
      if (
        event.message?.includes("Unexpected token '<'") &&
        event.error?.stack?.includes('cdn.cookielaw')
      ) {
        console.warn('Suppressed OneTrust JSON parse error:', event.message);
        event.preventDefault();
      }
    });
  }

  @HostListener('window:beforeunload')
  unloadHandler() {
    this.sb.unsubscribe();
  }

  getRouterLink() {
    const routeWithoutFragment = this.route.parseUrl(this.route.url);
    routeWithoutFragment.fragment = null;
    return routeWithoutFragment.toString();
  }

  onLinkClick(linkId: string) {
    this.focusManager.moveFocusStartingPoint(`#${linkId}`);
  }
}
